<template>
	<div class="image-uploader">
		<el-tooltip placement="top">
			<div slot="content" v-html="tooltipContent"></div>
			<span class="image-upload-btn" :class="{ 'is-disabled': uploadCount >= maxCount, 'chat-type-disabled': !canUpload }" @click="handleClick">
				<!-- 上传图片图标 -->
				<svg class="image-upload-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
					<circle cx="8.5" cy="8.5" r="1.5"></circle>
					<polyline points="21 15 16 10 5 21"></polyline>
				</svg>
			</span>
		</el-tooltip>
		<input ref="imageInput" type="file" accept="image/png,image/jpg,image/jpeg,image/bmp,image/webp" multiple style="display: none" @change="handleChange" />
	</div>
</template>

<script>
	export default {
		name: 'ImageUploader',
		data() {
			return {
				// 只追踪上传计数（用于按钮禁用），实际图片列表由父组件管理
				uploadCount: 0
			};
		},
		props: {
			// 待发送图片列表（v-model），元素格式 { url: 预览用 dataURL, base64: 发送用纯 base64 }
			value: {
				type: Array,
				default: () => []
			},
			// 最多上传张数
			maxCount: {
				type: Number,
				default: 5
			},
			// 单张大小上限（字节）
			maxSize: {
				type: Number,
				default: 50 * 1024 * 1024
			},
			// 是否允许上传（已登录 或 3种模式中任一有剩余次数）
			canUpload: {
				type: Boolean,
				default: true
			}
		},
		watch: {
			// 外部（父组件）清空图片时同步计数
			value: {
				immediate: true,
				handler(val) {
					this.uploadCount = (val || []).length;
				}
			}
		},
		computed: {
			maxSizeMB() {
				return Math.round(this.maxSize / 1024 / 1024);
			},
			tooltipContent() {
				if (!this.canUpload) {
					return '当前没有可用次数，请先登录';
				}
				if (this.uploadCount >= this.maxCount) {
					return `最多上传 ${this.maxCount} 张图片`;
				}
				return `支持上传图片<br/>最多 ${this.maxCount} 个（单个不超过 ${this.maxSizeMB} MB）`;
			}
		},
		methods: {
			handleClick() {
				if (!this.canUpload) {
					this.$message.warning('当前没有可用次数，请先登录');
					return;
				}
				if (this.uploadCount >= this.maxCount) {
					this.$message.warning(`每次最多上传 ${this.maxCount} 张图片`);
					return;
				}
				this.$refs.imageInput.click();
			},
			handleChange(event) {
				const files = Array.from(event.target.files || []);
				event.target.value = '';
				if (!files.length) return;
				this.processFiles(files);
			},
			// 供外部（如粘贴）调用的文件处理方法，包含完整校验 + base64 转换
			processFiles(files) {
				if (!files || !files.length) return;
				// 数量校验
				if (this.uploadCount + files.length > this.maxCount) {
					this.$message.warning(`每次最多上传 ${this.maxCount} 张图片`);
					return;
				}
				// 大小校验
				if (files.some(file => file.size > this.maxSize)) {
					this.$message.warning(`每张图片不能超过 ${this.maxSizeMB}MB`);
					return;
				}
				// 格式校验：仅允许 png / jpeg(jpg) / bmp / webp
				const allowedExtensions = ['png', 'jpeg', 'jpg', 'bmp', 'webp'];
				const invalidFile = files.find(file => {
					const ext = file.name.split('.').pop().toLowerCase();
					return !allowedExtensions.includes(ext);
				});
				if (invalidFile) {
					this.$message.warning('仅支持上传 png、jpeg、jpg、bmp、webp 格式 的文件');
					return;
				}
				// File 转 base64（异步读取）
				const tasks = files.map(file => {
					return new Promise((resolve, reject) => {
						const reader = new FileReader();
						reader.onload = e => {
							const dataUrl = e.target.result;
							resolve({
								url: dataUrl,
								base64: dataUrl.split(',')[1] || ''
							});
						};
						reader.onerror = () => reject(new Error('图片读取失败'));
						reader.readAsDataURL(file);
					});
				});
				Promise.all(tasks)
					.then(images => {
						this.uploadCount += images.length;
						// emit 新上传的图片列表，父组件自行拼接
						this.$emit('change', images);
					})
					.catch(() => {
						this.$message.error('图片读取失败，请重试');
					});
			},
			// 父组件移除图片时调用，同步计数
			removeOne() {
				this.uploadCount = Math.max(0, this.uploadCount - 1);
			},
		},
	};
</script>

<style lang="less" scoped>
	.image-uploader {
		display: inline-flex;
		align-items: center;
		margin-left: 4px;
	}

	.image-upload-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		border-radius: 50%;
		cursor: pointer;
		color: #2563eb;
        background-color: #edf3fe;
        background-color: rgb(233.2, 239.4, 253);
		transition: background 0.15s;
		outline: none;

		&:hover {
			background: #dbe4ff;
		}

		&.is-disabled {
			opacity: 0.35;
			cursor: not-allowed;

			&:hover {
				background: #eef2ff;
			}
		}

		&.chat-type-disabled {
			opacity: 0.7;
			cursor: not-allowed;

			&:hover {
				background: #eef2ff;
			}
		}

		.image-upload-icon-svg {
			width: 20px;
			height: 20px;
		}
	}
</style>
